import { CancelOrderDialogPortalProvider } from '@/components/OrdersModule/OrderDetail/CancelOrderDialog/contexts/Portal'
import OrderDetailFooter from '@/components/OrdersModule/OrderDetail/Footer/OrderDetailFooter'
import OrderDetailHeader from '@/components/OrdersModule/OrderDetail/Header/OrderDetailHeader'
import OrderDetailItemList from '@/components/OrdersModule/OrderDetail/OrderDetailItemList'
import { ColorEnum } from '@/constants/color'
import Overlay from '@/features/common/components/Overlay'
import { OVERLAY_DURATION_MS } from '@/features/common/constants'
import { ReprintModalPortalProvider } from '@/features/orders/components/OrderDetail/parts/Footer/ContextMenu/parts/ReprintModal/contexts/Portal'
import { State } from '@/features/orders/constants/app'
import DismissOverlayProvider from '@/features/ui/components/Dropdown/contexts/DismissOverlay'
import { useAppSelector } from '@/hooks/redux'
import { ordersSelectors } from '@/redux/reducers/orders/selectors'
import { hexToRgba } from '@/utils/hexToRgba'
import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, StyleSheet, View } from 'react-native'

interface OrderDetailProps {
    uuid: string
}

const OrderDetail: FC<OrderDetailProps> = ({ uuid }) => {
    const { t } = useTranslation()

    const order = useAppSelector(ordersSelectors.orderByUuid(uuid))

    const [isAcceptedOverlay, setIsAcceptedOverlay] = useState(false)
    const [isCancelledOverlay, setIsCancelledOverlay] = useState(false)

    const prevOrderState = useRef<State>(undefined)
    const acceptedOverlayTimeoutRef = useRef<number>(undefined)
    const cancelledOverlayTimeoutRef = useRef<number>(undefined)

    useEffect(() => {
        if (!order?.state) return

        if (prevOrderState.current === State.AwaitingAcceptance && order.state === State.Accepted) {
            setIsAcceptedOverlay(true)

            acceptedOverlayTimeoutRef.current = setTimeout(() => {
                setIsAcceptedOverlay(false)
            }, OVERLAY_DURATION_MS)
        }

        if (
            prevOrderState.current &&
            prevOrderState.current !== State.Cancelled &&
            order.state === State.Cancelled
        ) {
            setIsCancelledOverlay(true)

            cancelledOverlayTimeoutRef.current = setTimeout(() => {
                setIsCancelledOverlay(false)
            }, OVERLAY_DURATION_MS)
        }

        prevOrderState.current = order.state
    }, [order?.state])

    useEffect(() => {
        return () => {
            if (acceptedOverlayTimeoutRef.current) {
                clearTimeout(acceptedOverlayTimeoutRef.current)
            }
            if (cancelledOverlayTimeoutRef.current) {
                clearTimeout(cancelledOverlayTimeoutRef.current)
            }
        }
    }, [])

    if (!order) return

    return (
        <CancelOrderDialogPortalProvider>
            <ReprintModalPortalProvider>
                <DismissOverlayProvider>
                    <View style={styles.container}>
                        <ScrollView contentContainerStyle={styles.scrollContent}>
                            <OrderDetailHeader uuid={order.id} style={styles.header} />
                            <OrderDetailItemList
                                items={order.items}
                                containerItems={order.containerItems}
                                deliveryPrice={order.deliveryPrice}
                                smallOrderSurcharge={order.smallOrderSurcharge}
                                tip={order.tip}
                                totalPrice={order.price}
                                currency={order.currency}
                            />
                        </ScrollView>
                        <OrderDetailFooter orderUuid={order.id} />
                        {isAcceptedOverlay && (
                            <Overlay type="success" message={t('order.detail.accepted')} />
                        )}
                        {isCancelledOverlay && (
                            <Overlay type="cancel" message={t('order.detail.cancelled')} />
                        )}
                    </View>
                </DismissOverlayProvider>
            </ReprintModalPortalProvider>
        </CancelOrderDialogPortalProvider>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: ColorEnum.WHITE,
        elevation: 4,
        shadowColor: hexToRgba(ColorEnum.Black, 0.25),
        shadowRadius: 8,
        shadowOffset: {
            width: -4,
            height: 0,
        },
        shadowOpacity: 1,
    },
    header: {
        marginBottom: 16,
    },
    scrollContent: {
        paddingBottom: 8,
    },
})

export default OrderDetail
